import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";

const WINDOWS = [["week", "Next 7 days"], ["month", "Next month"], ["quarter", "Next quarter"]];

const KIND_LABEL = { change: "Planned change", outage: "Outage", blackout: "Blackout window",
                     freeze: "Change freeze", maintenance: "Maintenance window",
                     release: "Release window" };

const when = (value) => (value ? new Date(value).toLocaleString() : "open-ended");

export default function OperationsCalendar() {
  const [data, setData] = useState(null);
  const [providers, setProviders] = useState([]);
  const [window_, setWindow] = useState("month");
  const [site, setSite] = useState("");
  const [provider, setProvider] = useState("");
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const query = new URLSearchParams({ window: window_ });
    if (site) query.set("location_key", site);
    if (provider) query.set("provider_key", provider);
    apiFetch(`/calendar/operations?${query}`).then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setError(typeof body.detail === "string" ? body.detail : "Calendar unavailable");
                   setData(null); return; }
      setError(null); setData(body);
    });
  }, [window_, site, provider]);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/providers").then((r) => (r.ok ? r.json() : [])).then(setProviders);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const row = (entry) => (
    <tr key={`${entry.kind}-${entry.id}`} className="border-b last:border-0"
        data-testid={`calendar-entry-${entry.number || entry.id}`}>
      <th scope="row" className="px-3 py-2 text-left font-medium">
        {KIND_LABEL[entry.kind] || entry.kind}
        {entry.number && <span className="block font-mono text-xs">{entry.number}</span>}
      </th>
      <td className="px-3 py-2">{entry.title}</td>
      <td className="px-3 py-2">{when(entry.start)}</td>
      <td className="px-3 py-2">{when(entry.end)}</td>
      <td className="px-3 py-2">
        {entry.enterprise_wide ? "Enterprise-wide"
                               : entry.location_names.join(", ") || "not recorded"}
      </td>
      <td className="px-3 py-2">
        {entry.kind === "release" && (
          <span data-testid={`calendar-release-${entry.number || entry.id}`}>
            {entry.environment || "environment not recorded"} · {entry.state}
          </span>
        )}
        {entry.kind === "change" && (
          <>
            risk {entry.risk_level || "unscored"}
            {entry.cab_required && (
              <span className="ml-1 text-amber-800" data-testid={`calendar-cab-${entry.number}`}>
                · CAB
              </span>
            )}
          </>
        )}
        {entry.kind === "outage" && (
          <>
            {entry.state}
            {entry.major && (
              <span className="ml-1 text-red-800" data-testid={`calendar-major-${entry.number}`}>
                · major
              </span>
            )}
            {entry.clinical_impact && <span className="ml-1 text-red-800">· clinical impact</span>}
          </>
        )}
        {["blackout", "freeze", "maintenance"].includes(entry.kind) && "no changes permitted"}
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="calendar-title">
            Operations Calendar
          </h1>
          <p className="text-sm text-muted-foreground">
            What is coming: planned changes, outages and the windows when nothing may change.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="calendar-knowledge-link">
            <Link to="/knowledge">Knowledge</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        {error && <p className="text-sm text-destructive" data-testid="calendar-error">{error}</p>}

        <section className="grid gap-3 md:grid-cols-3" data-testid="calendar-filters">
          <div className="space-y-1">
            <label htmlFor="calendar-window" className="text-sm font-medium">Looking ahead</label>
            <select id="calendar-window" data-testid="calendar-window" value={window_}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => setWindow(e.target.value)}>
              {WINDOWS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="calendar-site" className="text-sm font-medium">Site</label>
            <select id="calendar-site" data-testid="calendar-site" value={site}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => setSite(e.target.value)}>
              <option value="">Every site</option>
              {(data?.sites || []).map((s) => (
                <option key={s.key} value={s.key}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="calendar-provider" className="text-sm font-medium">Provider</label>
            <select id="calendar-provider" data-testid="calendar-provider" value={provider}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => setProvider(e.target.value)}>
              <option value="">Every provider</option>
              {providers.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select>
          </div>
        </section>

        {data && (
          <>
            <p className="text-sm text-muted-foreground" data-testid="calendar-method">
              {data.definition} Method: {data.method}
            </p>
            <section className="grid gap-3 md:grid-cols-4 text-sm" data-testid="calendar-totals">
              {[["Entries", data.totals.entries, "entries"],
                ["Planned changes", data.totals.changes, "changes"],
                ["Outages", data.totals.outages, "outages"],
                ["Restricted windows", data.totals.restricted_windows, "windows"]].map(
                ([label, value, id]) => (
                  <div key={id} className="border rounded-md px-3 py-2"
                       data-testid={`calendar-total-${id}`}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-2xl font-semibold">{value}</p>
                  </div>
                ))}
            </section>

            {data.my_site && (
              <section className="space-y-2" data-testid="calendar-my-site">
                <h2 className="text-base md:text-lg font-medium">
                  Coming at {data.my_site_name || data.my_site}
                </h2>
                <ul className="text-sm space-y-1">
                  {data.at_my_site.slice(0, 12).map((entry) => (
                    <li key={`mine-${entry.kind}-${entry.id}`}
                        data-testid={`calendar-my-site-${entry.number || entry.id}`}>
                      {KIND_LABEL[entry.kind] || entry.kind}: {entry.title} — {when(entry.start)}
                      {entry.enterprise_wide && " (enterprise-wide)"}
                    </li>
                  ))}
                  {data.at_my_site.length > 12 && (
                    <li className="text-muted-foreground" data-testid="calendar-my-site-more">
                      and {data.at_my_site.length - 12} more in this window — see the full table
                      below.
                    </li>
                  )}
                  {data.at_my_site.length === 0 && (
                    <li className="text-muted-foreground" data-testid="calendar-my-site-empty">
                      Nothing planned for your site in this window.
                    </li>
                  )}
                </ul>
              </section>
            )}

            <section className="space-y-2" data-testid="calendar-table">
              <h2 className="text-base md:text-lg font-medium">Everything in this window</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border rounded-md">
                  <caption className="sr-only">
                    Planned changes, outages and restricted windows
                  </caption>
                  <thead>
                    <tr className="text-left border-b">
                      <th scope="col" className="px-3 py-2">What</th>
                      <th scope="col" className="px-3 py-2">Title</th>
                      <th scope="col" className="px-3 py-2">Starts</th>
                      <th scope="col" className="px-3 py-2">Ends</th>
                      <th scope="col" className="px-3 py-2">Where</th>
                      <th scope="col" className="px-3 py-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries.map(row)}
                    {data.entries.length === 0 && (
                      <tr><td colSpan="6" className="px-3 py-2 text-muted-foreground"
                              data-testid="calendar-empty">
                        Nothing is scheduled in this window for these filters.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {data.entries_filtered_out > 0 && (
                <p className="text-sm text-muted-foreground" data-testid="calendar-filtered-out">
                  {data.entries_filtered_out} entr{data.entries_filtered_out === 1 ? "y" : "ies"}{" "}
                  outside these filters are not shown.
                </p>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
